import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import { IBudgetProgramAddEditRequest } from '../../../../model/sge/budget-program/request/budget-program-add-edit-request';
import { IBudgetProgramResponse } from '../../../../model/sge/budget-program/response/budget-program-response';
import { IBudgetUnitResponse } from '../../../../model/sge/budget-unit/response/budget-unit-response';
import { hasErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { formatCodeName } from '../../components/code-name-label';
import { toBudgetProgramForm, validateBudgetProgram } from './budget-program-form-utils';

interface BudgetProgramDrawerProps {
    open: boolean;
    initial?: IBudgetProgramResponse;
    budgetUnits: IBudgetUnitResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IBudgetProgramAddEditRequest) => void;
}

export function BudgetProgramDrawer({ open, initial, budgetUnits, isSaving, onClose, onSave }: BudgetProgramDrawerProps) {
    const [form, setForm] = useState<IBudgetProgramAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateBudgetProgram(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toBudgetProgramForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IBudgetProgramAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, code: form.code!.trim(), name: form.name!.trim() });
    }

    return (
        <FormDrawer
            open={open}
            icon={<ListAltIcon />}
            title={isEdit ? 'Editar programa orçamentário' : 'Novo programa orçamentário'}
            subtitle={initial ? formatCodeName(initial) : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar programa'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Código</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.code || ''} onChange={(e) => update({ code: e.target.value })} error={!!errors.code} />
                        <FieldError message={errors.code} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Nome</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.name || ''} onChange={(e) => update({ name: e.target.value })} error={!!errors.name} />
                        <FieldError message={errors.name} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Vinculação">
                <InputLabel required>Unidade orçamentária</InputLabel>
                <Select
                    fullWidth
                    disabled={isEdit}
                    value={form.budgetUnitId ?? ''}
                    onChange={(e) => update({ budgetUnitId: parseNumberInput(e.target.value) })}
                    error={!!errors.budgetUnitId}
                >
                    <MenuItem value=""><em>Selecionar</em></MenuItem>
                    {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{formatCodeName(u)}</MenuItem>)}
                </Select>
                <FieldError message={errors.budgetUnitId} />
                {isEdit && <FormHelperText>A unidade orçamentária não pode ser alterada</FormHelperText>}
            </FormDrawerGroup>
        </FormDrawer>
    );
}
